import { useLocation, useNavigate } from "react-router";
import { useQueryClient } from "@/lib/query";
import { LogOut } from "lucide-react";
import type { ReactNode } from "react";

import { MotionPage } from "@/components/common/motion";
import { DoctorStatusBadge } from "@/components/common/status-badge";
import { Topbar } from "@/components/common/topbar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/common/app-sidebar";
import { ActiveCallBar } from "@/components/doctor/video-call/active-call-bar";
import { useAuth } from "@/contexts/AuthContext";
import { initials } from "@/lib/format";
import { DOCTOR_NAV } from "@/lib/nav";
import type { Doctor } from "@/types/doctor";

export function AppShell({ doctor, children }: { doctor: Doctor; children: ReactNode }) {
  const { logOut } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Keying on the path replays the entrance on every navigation, so the shell
  // stays put while the page beneath it slides in.
  const { pathname } = useLocation();

  const handleSignOut = async () => {
    await queryClient.cancelQueries();
    queryClient.clear();
    await logOut();
    navigate("/login", { replace: true });
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar
          items={DOCTOR_NAV}
          homeUrl="/dashboard"
          footerTitle="HealNova Doctor Portal"
          footerSubtitle="Care, coordinated."
        />
        <SidebarInset className="bg-background">
          <Topbar>
            <DoctorStatusBadge status={doctor.status} className="hidden sm:inline-flex" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-full p-0.5 transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                  aria-label="Account menu"
                >
                  <Avatar className="size-9 border border-border">
                    {doctor.profilePicture && (
                      <AvatarImage src={doctor.profilePicture} alt={doctor.name} />
                    )}
                    <AvatarFallback className="bg-primary text-sm font-semibold text-primary-foreground">
                      {initials(doctor.name)}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl">
                <DropdownMenuLabel className="space-y-0.5">
                  <p className="truncate text-sm font-semibold">{doctor.name}</p>
                  <p className="truncate text-xs font-normal text-muted-foreground">
                    {doctor.specialization}
                  </p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => navigate("/profile")}>
                  View profile
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate("/settings")}>Settings</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => void handleSignOut()}>
                  <LogOut className="size-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </Topbar>

          {/* Sits above the page and outside the keyed MotionPage, so it does
              not replay its entrance on every navigation while a call runs. */}
          <ActiveCallBar />

          <MotionPage as="main" key={pathname} className="flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
            {children}
          </MotionPage>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}

export function ShellSignOutButton() {
  const { logOut } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return (
    <Button
      variant="outline"
      onClick={async () => {
        await queryClient.cancelQueries();
        queryClient.clear();
        await logOut();
        navigate("/login", { replace: true });
      }}
    >
      <LogOut className="size-4" /> Sign out
    </Button>
  );
}
